import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Timer, Target, BookOpen, ChevronRight, Lock, Zap } from "lucide-react";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

const typeLabel = { full_exam: "Esame casuale", simulation: "Simulazione ufficiale", block_sim: "Simulazione blocco", module_quiz: "Quiz modulo", lesson_quiz: "Quiz lezione", review_quiz: "Ripasso errori", sample_sim: "Sim. campione" };

const LockBadge = () => (
  <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] text-[color:var(--text-2)] font-extrabold text-xs px-4 py-2.5" aria-hidden="true">
    <Lock className="w-3.5 h-3.5" /> Sblocca
  </span>
);

export default function Simulazioni() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const locked = Boolean(user && !user.hasFullAccess);
  const [sims, setSims] = useState(null);
  const [blocks, setBlocks] = useState(null);
  const [modules, setModules] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [selModule, setSelModule] = useState("");

  useEffect(() => {
    api.get("/simulations").then((r) => setSims(r.data)).catch(() => setSims([]));
    api.get("/blocks").then((r) => setBlocks(r.data)).catch(() => setBlocks([]));
    api.get("/modules").then((r) => setModules(r.data)).catch(() => setModules([]));
    api.get("/exams/attempts").then((r) => setAttempts(r.data)).catch(() => setAttempts([]));
  }, []);

  return (
    <div className="pt-6">
      <h1 className="text-3xl font-extrabold tracking-tight fade-up">Allénati.</h1>
      <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6 fade-up">
        Simula l'esame vero o allena un singolo blocco. Nessuna penalità per gli errori.
      </p>

      {/* Rules intro */}
      <div className="mt-5 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5 fade-up" data-testid="simulations-rules-intro">
        <p className="text-xs font-extrabold tracking-[0.14em] uppercase">Prima di iniziare</p>
        <ul className="mt-2.5 space-y-1.5 text-sm leading-6 text-[color:var(--text-2)]">
          <li><span className="font-mono font-semibold text-[color:var(--ink)]">60Q · 60'</span> — quattro blocchi da 15 domande, timer unico.</li>
          <li><span className="font-mono font-semibold text-[color:var(--ink)]">9/15</span> — in <strong className="text-[color:var(--ink)]">ciascun</strong> blocco per essere idoneo. Il totale non basta.</li>
          <li><span className="font-mono font-semibold text-[color:var(--ink)]">0</span> — penalità: rispondere conviene sempre.</li>
        </ul>
      </div>

      {/* Free sample simulation — trial users */}
      {locked && (
        <section className="mt-5 rounded-3xl border-2 border-[color:var(--volt)] bg-white p-5 fade-up" data-testid="simulations-sample-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--text-2)]">Gratis · Assaggio</p>
              <h2 className="mt-1.5 text-lg font-extrabold tracking-tight">Simulazione campione</h2>
              <p className="mt-1.5 text-sm text-[color:var(--text-2)] leading-5">20 domande, 5 per blocco. Scopri il tuo punto debole prima di sbloccare tutto.</p>
            </div>
            <Zap className="w-7 h-7 shrink-0 text-[color:var(--ink)]" fill="var(--volt)" aria-hidden="true" />
          </div>
          <button
            onClick={() => navigate("/esame?mode=sample")}
            data-testid="simulations-start-sample-button"
            className="press mt-4 w-full rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-5 py-3 min-h-[44px] hover:brightness-95 transition-all duration-200"
          >
            Prova gratis
          </button>
        </section>
      )}

      {/* Official simulations */}
      <section className="mt-6 rounded-3xl bg-[color:var(--ink)] text-white p-6 fade-up" data-testid="simulations-official-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--volt)]">Performance mode</p>
            <h2 className="mt-2 text-xl font-extrabold tracking-tight">Simulazioni ufficiali</h2>
            <p className="mt-2 font-mono text-xs text-white/70">60Q · 60 min · 4 blocchi · soglia 9/15 in ciascuno</p>
          </div>
          <Timer className="w-8 h-8 shrink-0 text-[color:var(--volt)]" />
        </div>
        <div className="mt-5 space-y-2">
          {!sims && <div className="rounded-2xl bg-white/5 h-14 animate-pulse" />}
          {sims && sims.length === 0 && (
            <p className="text-sm text-white/60">Nessuna simulazione disponibile al momento.</p>
          )}
          {sims?.map((s) => {
            const good = s.lastAttempt && s.lastAttempt.esito === "IDONEO";
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3" data-testid={`simulation-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="font-extrabold text-sm tracking-tight">{s.titolo}</p>
                  {s.lastAttempt ? (
                    <p className="font-mono text-[11px] text-white/60 mt-0.5">
                      Ultimo esito:{" "}
                      <span className={good ? "text-[color:var(--volt)] font-semibold" : "text-[#ff7a7f] font-semibold"}>{s.lastAttempt.esito}</span>{" "}
                      ({s.lastAttempt.totalCorrect}/{s.lastAttempt.totalQuestions})
                    </p>
                  ) : (
                    <p className="font-mono text-[11px] text-white/50 mt-0.5">Mai provata</p>
                  )}
                </div>
                <button
                  onClick={() => navigate(locked ? "/sblocca" : `/esame?mode=simulation&simulationId=${s.id}`)}
                  disabled={!s.valid && !locked}
                  data-testid={`simulation-start-${s.id}-button`}
                  className={`press shrink-0 rounded-full font-extrabold text-sm px-5 py-2.5 min-h-[44px] transition-all duration-200 ${
                    locked
                      ? "bg-white/10 text-white/70 border border-white/15 hover:bg-white/15 inline-flex items-center gap-1.5"
                      : "bg-[color:var(--volt)] text-[color:var(--ink)] hover:brightness-95 disabled:opacity-40"
                  }`}
                >
                  {locked ? (
                    <>
                      <Lock className="w-3.5 h-3.5" aria-hidden="true" /> Sblocca
                    </>
                  ) : s.lastAttempt ? (
                    "Riprova"
                  ) : (
                    "Inizia"
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Random full exam */}
      <Link
        to={locked ? "/sblocca" : "/esame?mode=full"}
        data-testid="simulations-full-exam-card"
        className="mt-4 block rounded-3xl border border-[color:var(--hairline)] bg-white p-5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200 fade-up"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-extrabold tracking-tight">Esame casuale</h2>
            <p className="mt-1 text-sm text-[color:var(--text-2)] leading-5">60 domande pescate a caso dall'archivio: ogni volta diverso. Stesse regole dell'esame vero.</p>
            <p className="mt-2 font-mono text-[11px] text-[color:var(--text-2)]">60Q · 60 min · 9/15 per blocco</p>
          </div>
          {locked ? <LockBadge /> : <span className="shrink-0 rounded-full bg-[color:var(--ink)] text-white font-extrabold text-sm px-5 py-2.5">Inizia</span>}
        </div>
      </Link>

      {/* Block simulation */}
      <section className="mt-8 fade-up">
        <div className="flex items-center gap-2 mb-1">
          <Target className="w-4 h-4" />
          <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase">Simula un blocco</h2>
        </div>
        <p className="text-xs text-[color:var(--text-2)] mb-4">15 domande · 15 minuti · stessa soglia 9/15.</p>
        <div className="grid grid-cols-2 gap-3" data-testid="simulations-block-card">
          {!blocks && [1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 rounded-3xl" />)}
          {blocks?.map((b) => (
            <button
              key={b.blocco}
              onClick={() => navigate(locked ? "/sblocca" : `/esame?mode=block&blocco=${b.blocco}`)}
              data-testid={`simulations-block-${b.blocco}-button`}
              className="press text-left rounded-3xl border border-[color:var(--hairline)] bg-white p-4 min-h-[44px] hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
            >
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-2)]">Blocco {b.blocco}</p>
                {locked && <Lock className="w-3.5 h-3.5 text-[color:var(--text-2)]" aria-hidden="true" />}
              </div>
              <p className="mt-1 font-extrabold text-sm leading-5">{b.nome}</p>
              <p className="mt-2 font-mono text-[11px] text-[color:var(--text-2)]">{b.questionCount} domande in archivio</p>
            </button>
          ))}
        </div>
      </section>

      {/* Module quiz */}
      <section className="mt-8 fade-up" data-testid="simulations-module-quiz-card">
        <div className="flex items-center gap-2 mb-1">
          <BookOpen className="w-4 h-4" />
          <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase">Quiz per modulo</h2>
        </div>
        <p className="text-xs text-[color:var(--text-2)] mb-4">Fino a 10 domande da un solo modulo.</p>
        <div className="rounded-3xl border border-[color:var(--hairline)] bg-white p-4">
          <Select value={selModule} onValueChange={setSelModule}>
            <SelectTrigger
              data-testid="simulations-module-select"
              aria-label="Scegli un modulo per il quiz"
              className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3 min-h-[48px] text-sm focus:ring-2 focus:ring-[color:var(--ink)]"
            >
              <SelectValue placeholder="Scegli un modulo…" />
            </SelectTrigger>
            <SelectContent className="rounded-2xl">
              {modules?.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`simulations-module-option-${m.id}`}>
                  {m.titolo} — Blocco {m.blocco}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            disabled={!selModule && !locked}
            onClick={() => navigate(locked ? "/sblocca" : `/esame?mode=module&moduleId=${selModule}`)}
            data-testid="simulations-start-module-quiz-button"
            className="press mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3 min-h-[44px] disabled:opacity-40 hover:bg-black/85 transition-colors duration-200"
          >
            {locked && <Lock className="w-3.5 h-3.5" aria-hidden="true" />}
            {locked ? "Sblocca il quiz per modulo" : "Inizia il quiz"}
          </button>
        </div>
      </section>

      {/* Review errors */}
      <Link
        to={locked ? "/sblocca" : "/ripasso"}
        data-testid="simulations-review-errors-card"
        className="mt-8 block rounded-3xl border border-[color:var(--hairline)] bg-white p-5 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200 fade-up"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-extrabold tracking-tight">Ripasso errori</h2>
            <p className="mt-1 text-sm text-[color:var(--text-2)] leading-5">Le domande che hai sbagliato, filtrabili per blocco. Rifalle finché non escono dalla lista.</p>
          </div>
          {locked ? <Lock className="w-5 h-5 text-[color:var(--text-2)] shrink-0" aria-hidden="true" /> : <ChevronRight className="w-5 h-5 text-[color:var(--text-2)] shrink-0" />}
        </div>
      </Link>

      {/* History */}
      <section className="mt-8 fade-up">
        <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase mb-4">Ultimi risultati</h2>
        {!attempts && <Skeleton className="h-20 rounded-3xl" />}
        {attempts && attempts.length === 0 && (
          <p className="text-sm text-[color:var(--text-2)] py-6 text-center rounded-3xl border border-dashed border-[color:var(--hairline)]">
            Ancora nessuna simulazione. Inizia dalla prima.
          </p>
        )}
        <div className="space-y-2" data-testid="simulations-history">
          {attempts?.slice(0, 8).map((a) => {
            const isSample = a.type === "sample_sim";
            const good = a.esito === "IDONEO" || a.esito === "SUPERATO";
            return (
              <div key={a.id} className="flex items-center justify-between rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold">{typeLabel[a.type] || a.type}{a.blocco ? ` · Blocco ${a.blocco}` : ""}</p>
                  <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-0.5">{fmtDate(a.date)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm font-semibold">{a.totalCorrect}/{a.totalQuestions}</p>
                  <p className={`text-[10px] font-extrabold uppercase tracking-wide ${good || isSample ? "" : "text-[color:var(--danger)]"}`}>
                    {isSample ? (
                      <span className="inline-block rounded-full bg-[color:var(--bg-subtle)] border border-[color:var(--hairline)] px-2 py-0.5 text-[color:var(--text-2)]">Assaggio</span>
                    ) : good ? (
                      <span className="inline-block rounded-full bg-[color:var(--volt)] px-2 py-0.5">{a.esito}</span>
                    ) : (
                      a.esito
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
